import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Star, Loader2, Check, Trash2, ShieldAlert } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// @seo-check: noindex-ok
export const Route = createFileRoute("/admin/avaliacoes")({
  head: () => ({
    meta: [
      { title: "Moderação de avaliações | MEI Toolbox" },
      {
        name: "description",
        content:
          "Área administrativa interna do MEI Toolbox para moderação da fila de avaliações dos usuários.",
      },
      { name: "robots", content: "noindex,nofollow" },
    ],
    links: [{ rel: "canonical", href: "https://meitoolbox.com.br/admin/avaliacoes" }],
  }),
  component: AdminRatingsPage,
});

type Row = {
  id: string;
  rating: number;
  user_id: string;
  comment: string | null;
  created_at: string;
  status: "approved" | "pending" | "rejected";
  flag_reason: string | null;
  content_type: "article" | "tool";
  content_slug: string;
};

type Profile = { id: string; display_name: string | null };

function AdminRatingsPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [tab, setTab] = useState<"pending" | "approved" | "rejected">("pending");
  const [rows, setRows] = useState<Row[]>([]);
  const [profiles, setProfiles] = useState<Record<string, Profile>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);

  // Check admin role
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setIsAdmin(false);
      return;
    }
    supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", user.id)
      .eq("role", "admin")
      .maybeSingle()
      .then(({ data }) => setIsAdmin(!!data));
  }, [user, authLoading]);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("ratings")
      .select(
        "id, rating, user_id, comment, created_at, status, flag_reason, content_type, content_slug",
      )
      .eq("status", tab)
      .order("created_at", { ascending: false })
      .limit(200);
    if (error) {
      toast.error("Erro ao carregar avaliações");
      setLoading(false);
      return;
    }
    const list = (data ?? []) as Row[];
    setRows(list);
    const ids = Array.from(new Set(list.map((r) => r.user_id)));
    if (ids.length) {
      const { data: profs } = await supabase
        .from("profiles")
        .select("id, display_name")
        .in("id", ids);
      const map: Record<string, Profile> = {};
      (profs ?? []).forEach((p) => (map[p.id] = p as Profile));
      setProfiles(map);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin, tab]);

  const update = async (id: string, status: "approved" | "rejected") => {
    setBusy(id);
    const { error } = await supabase.from("ratings").update({ status }).eq("id", id);
    setBusy(null);
    if (error) {
      toast.error("Falha ao atualizar status");
      return;
    }
    toast.success(status === "approved" ? "Avaliação aprovada" : "Avaliação rejeitada");
    setRows((prev) => prev.filter((r) => r.id !== id));
  };

  const confirmDelete = (id: string) => setConfirmId(id);

  const remove = async () => {
    if (!confirmId) return;
    setBusy(confirmId);
    const { error } = await supabase.from("ratings").delete().eq("id", confirmId);
    setBusy(null);
    setConfirmId(null);
    if (error) {
      console.error("[admin] delete error:", error);
      toast.error(`Falha ao excluir: ${error.message}`, { duration: 6000 });
      return;
    }
    toast.success("Avaliação excluída");
    setRows((prev) => prev.filter((r) => r.id !== confirmId));
  };

  if (authLoading || isAdmin === null) {
    return (
      <main className="container mx-auto px-4 py-20 flex justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </main>
    );
  }

  if (!user) {
    return (
      <main className="container mx-auto px-4 py-20 max-w-md text-center">
        <ShieldAlert className="h-10 w-10 mx-auto text-muted-foreground mb-4" />
        <h1 className="text-xl font-bold mb-2">Login necessário</h1>
        <p className="text-sm text-muted-foreground mb-4">Entre para acessar a moderação.</p>
        <button
          onClick={() => navigate({ to: "/" })}
          className="rounded-full bg-primary text-primary-foreground px-5 py-2 text-sm font-semibold"
        >
          Voltar à home
        </button>
      </main>
    );
  }

  if (!isAdmin) {
    return (
      <main className="container mx-auto px-4 py-20 max-w-md text-center">
        <ShieldAlert className="h-10 w-10 mx-auto text-destructive mb-4" />
        <h1 className="text-xl font-bold mb-2">Acesso restrito</h1>
        <p className="text-sm text-muted-foreground">Esta área é exclusiva para administradores.</p>
      </main>
    );
  }

  return (
    <main className="container mx-auto px-4 py-12 max-w-4xl">
      <header className="mb-8">
        <p className="text-xs font-bold uppercase tracking-widest text-primary mb-2">Moderação</p>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">Fila de avaliações</h1>
        <p className="text-sm text-muted-foreground">
          Avaliações com links ou padrões suspeitos ficam aqui até serem revisadas.
        </p>
      </header>

      <div className="flex gap-2 border-b border-border mb-6">
        {(["pending", "approved", "rejected"] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 transition ${
              tab === t
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t === "pending" ? "Pendentes" : t === "approved" ? "Aprovadas" : "Rejeitadas"}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : rows.length === 0 ? (
        <p className="text-center text-sm text-muted-foreground py-12">Nada por aqui.</p>
      ) : (
        <ul className="space-y-4">
          {rows.map((r) => {
            const author = profiles[r.user_id]?.display_name ?? r.user_id.slice(0, 8);
            const url =
              r.content_type === "tool"
                ? `/ferramentas/${r.content_slug}`
                : `/artigos/${r.content_slug}`;
            return (
              <li key={r.id} className="rounded-2xl border border-border bg-card p-5 shadow-soft">
                <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                  <div>
                    <div className="flex items-center gap-1 mb-1">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star
                          key={n}
                          className={`h-4 w-4 ${
                            r.rating >= n
                              ? "fill-amber-400 text-amber-400"
                              : "text-muted-foreground/30"
                          }`}
                        />
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      <span className="font-medium text-foreground">{author}</span> em{" "}
                      <a
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        className="underline hover:text-primary"
                      >
                        {r.content_type === "tool" ? "ferramenta" : "artigo"} /{r.content_slug}
                      </a>{" "}
                      · {new Date(r.created_at).toLocaleString("pt-BR")}
                    </p>
                    {r.flag_reason && (
                      <p className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-amber-600 dark:text-amber-400">
                        Sinalizada: {r.flag_reason}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    {r.status !== "approved" && (
                      <button
                        onClick={() => update(r.id, "approved")}
                        disabled={busy === r.id}
                        className="inline-flex items-center gap-1 rounded-full bg-emerald-600 text-white px-3 py-1.5 text-xs font-semibold hover:opacity-90 transition disabled:opacity-60"
                      >
                        <Check className="h-3.5 w-3.5" /> Publicar
                      </button>
                    )}
                    {r.status !== "rejected" && (
                      <button
                        onClick={() => update(r.id, "rejected")}
                        disabled={busy === r.id}
                        className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs font-semibold hover:bg-muted transition disabled:opacity-60"
                      >
                        Rejeitar
                      </button>
                    )}
                    <button
                      onClick={() => confirmDelete(r.id)}
                      disabled={busy === r.id}
                      className="inline-flex items-center gap-1 rounded-full border border-destructive/40 text-destructive px-3 py-1.5 text-xs font-semibold hover:bg-destructive/10 transition disabled:opacity-60"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Excluir
                    </button>
                  </div>
                </div>
                {r.comment ? (
                  <p className="text-sm text-foreground/90 whitespace-pre-wrap break-words rounded-lg bg-muted/40 p-3">
                    {r.comment}
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground italic">(sem comentário)</p>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <AlertDialog open={!!confirmId} onOpenChange={(open) => !open && setConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir avaliação?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. A avaliação será removida permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setConfirmId(null)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={remove}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
}
